import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Server, Users, Music2, Activity, Mic2, TerminalSquare, Star, LifeBuoy, Crown } from "lucide-react";
import api from "../../api";
import { PageTitle, StatCard, Badge } from "./AdminLayout";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-white/40 font-mono text-sm">Loading overview…</p>;

  const { stats, bot, counts } = data;

  return (
    <div data-testid="admin-dashboard">
      <PageTitle title="Dashboard" sub="The entire GrooveMusic platform at a glance." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Servers" value={stats.servers.toLocaleString()} icon={Server} testId="dash-stat-servers" />
        <StatCard label="Users" value={stats.users.toLocaleString()} icon={Users} tone="text-volt" testId="dash-stat-users" />
        <StatCard label="Songs played" value={stats.songs_played.toLocaleString()} icon={Music2} tone="text-emerald-400" testId="dash-stat-songs" />
        <StatCard label="Uptime" value={`${stats.uptime}%`} icon={Activity} tone="text-emerald-400" testId="dash-stat-uptime" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Active voice" value={(bot.active_voice || 0).toLocaleString()} icon={Mic2} tone="text-volt" testId="dash-stat-voice" />
        <StatCard label="Commands used" value={(bot.commands_used || 0).toLocaleString()} icon={TerminalSquare} testId="dash-stat-commands" />
        <StatCard label="Shards" value={bot.shards} icon={Server} tone="text-amber-400" testId="dash-stat-shards" />
        <StatCard label="Premium subs" value={counts.premium_subs} icon={Crown} tone="text-amber-400" testId="dash-stat-premium" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <div className="rounded-lg border border-white/5 bg-panel p-6" data-testid="dash-bot-status">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Bot management</p>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-white/50">Status</span><Badge tone={bot.status === "online" ? "green" : "red"}>{bot.status}</Badge></div>
            <div className="flex justify-between"><span className="text-white/50">Version</span><span className="font-mono text-white/80">{bot.version}</span></div>
            <div className="flex justify-between"><span className="text-white/50">Activity</span><span className="font-mono text-white/80 text-xs truncate max-w-[160px]">{bot.activity}</span></div>
            <div className="flex justify-between"><span className="text-white/50">API</span><Badge tone="green">operational</Badge></div>
            <div className="flex justify-between"><span className="text-white/50">Maintenance</span><Badge tone={data.maintenance_mode ? "amber" : "grey"}>{data.maintenance_mode ? "on" : "off"}</Badge></div>
          </div>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-6" data-testid="dash-attention">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Needs attention</p>
          <div className="space-y-3">
            <Link to="/admin/reviews" className="flex items-center justify-between text-sm group" data-testid="dash-pending-reviews">
              <span className="flex items-center gap-2 text-white/60 group-hover:text-white transition-colors duration-150"><Star size={14} className="text-amber-400" /> Pending reviews</span>
              <Badge tone={counts.pending_reviews ? "amber" : "grey"}>{counts.pending_reviews}</Badge>
            </Link>
            <Link to="/admin/tickets" className="flex items-center justify-between text-sm group" data-testid="dash-open-tickets">
              <span className="flex items-center gap-2 text-white/60 group-hover:text-white transition-colors duration-150"><LifeBuoy size={14} className="text-volt" /> Open tickets</span>
              <Badge tone={counts.open_tickets ? "cyan" : "grey"}>{counts.open_tickets}</Badge>
            </Link>
            <Link to="/admin/moderation" className="flex items-center justify-between text-sm group" data-testid="dash-open-reports">
              <span className="flex items-center gap-2 text-white/60 group-hover:text-white transition-colors duration-150"><Activity size={14} className="text-red-400" /> Open reports</span>
              <Badge tone={counts.open_reports ? "red" : "grey"}>{counts.open_reports}</Badge>
            </Link>
          </div>
        </div>

        <div className="rounded-lg border border-white/5 bg-panel p-6" data-testid="dash-recent-audit">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Recent admin actions</p>
          <div className="space-y-2.5">
            {data.recent_audit.slice(0, 5).map((a) => (
              <div key={a.id} className="text-xs">
                <span className="font-mono text-volt">{a.action}</span>
                <span className="text-white/40"> · {a.actor}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-white/5 bg-panel p-6" data-testid="dash-recent-logs">
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">Live system feed</p>
        <div className="space-y-1.5 font-mono text-xs">
          {data.recent_logs.map((l) => (
            <div key={l.id} className="flex gap-3">
              <span className={l.level === "ERROR" ? "text-red-400" : l.level === "WARN" ? "text-amber-400" : "text-emerald-400"}>{l.level.padEnd(5)}</span>
              <span className="text-white/30">{l.source}</span>
              <span className="text-white/70 truncate">{l.message}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
